import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Star } from 'lucide-react';

interface Advantage {
  id: number;
  icon: string;
  title: string;
  description: string;
}

const ProfileAdvantageEditor: React.FC = () => {
  const navigate = useNavigate();
  const { advantageId } = useParams<{ advantageId?: string }>();
  const isEditing = advantageId && advantageId !== 'new';

  const [advantage, setAdvantage] = useState<Advantage>({
    id: 0,
    icon: 'Star',
    title: '',
    description: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEditing) {
      // In real app, fetch advantage data from API
      // For now, use mock data
      const mockAdvantage = {
        id: parseInt(advantageId),
        icon: 'MapPin',
        title: 'Lokasi Strategis',
        description: 'Terletak di jantung Jawa Tengah dengan akses mudah ke berbagai kota besar'
      };
      setAdvantage(mockAdvantage);
    }
  }, [isEditing, advantageId]);

  const handleSave = async () => {
    if (!advantage.title.trim() || !advantage.description.trim()) {
      alert('Mohon lengkapi semua field yang wajib diisi');
      return;
    }

    setIsLoading(true);
    try {
      // In real app, save to API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Show success message
      alert(isEditing ? 'Keunggulan berhasil diperbarui' : 'Keunggulan berhasil ditambahkan');
      
      // Navigate back to content management
      navigate('/admin/content-management');
    } catch (error) {
      alert('Terjadi kesalahan saat menyimpan data');
    } finally {
      setIsLoading(false);
    }
  };

  const iconOptions = [
    { value: 'MapPin', label: '📍 Lokasi' },
    { value: 'TrendingUp', label: '📈 Ekonomi' },
    { value: 'Users', label: '👥 SDM' },
    { value: 'Zap', label: '⚡ Infrastruktur' },
    { value: 'Shield', label: '🛡️ Keamanan' },
    { value: 'Star', label: '⭐ Kualitas' },
    { value: 'Award', label: '🏆 Prestasi' },
    { value: 'Target', label: '🎯 Fokus' },
    { value: 'Globe', label: '🌐 Global' },
    { value: 'Heart', label: '❤️ Kesejahteraan' }
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px', borderBottom: '2px solid #e5e7eb', paddingBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <button 
            onClick={() => navigate('/admin/content-management')}
            style={{ 
              background: 'none', 
              border: '1px solid #d1d5db', 
              color: '#374151', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali
          </button>
        </div>
        <h1 style={{ margin: '0', color: '#111827', fontSize: '24px' }}>
          {isEditing ? 'Edit Keunggulan Investasi' : 'Tambah Keunggulan Investasi'}
        </h1>
        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>
          {isEditing ? 'Perbarui informasi keunggulan investasi' : 'Tambahkan keunggulan investasi baru'}
        </p>
      </div>

      <div style={{ background: '#f9fafb', padding: '30px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr', marginBottom: '30px' }}>
          {/* Left Column - Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Judul Keunggulan *
              </label>
              <input
                type="text"
                value={advantage.title}
                onChange={(e) => setAdvantage({ ...advantage, title: e.target.value })}
                placeholder="Masukkan judul keunggulan"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Icon
              </label>
              <select
                value={advantage.icon}
                onChange={(e) => setAdvantage({ ...advantage, icon: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              >
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Deskripsi *
              </label>
              <textarea
                value={advantage.description}
                onChange={(e) => setAdvantage({ ...advantage, description: e.target.value })}
                placeholder="Masukkan deskripsi keunggulan"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '100px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
          </div>

          {/* Right Column - Preview */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
              Preview
            </label>
            <div style={{ 
              background: 'white', 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ 
                  padding: '8px', 
                  background: '#1e3a8a', 
                  color: 'white', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px',
                  height: '40px'
                }}>
                  <Star className="w-5 h-5" />
                </div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                  {advantage.title || 'Judul Keunggulan'}
                </h3>
              </div>
              <p style={{ 
                margin: 0, 
                fontSize: '14px', 
                color: '#6b7280', 
                lineHeight: '1.5',
                paddingLeft: '52px'
              }}>
                {advantage.description || 'Deskripsi keunggulan akan muncul di sini'}
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
          <button
            onClick={() => navigate('/admin/content-management')}
            disabled={isLoading}
            style={{
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            <Save className="w-4 h-4" />
            {isLoading ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileAdvantageEditor;